import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useMsal } from '@azure/msal-react';

import type { UserSessionContextState } from '../contexts/UserSessionContext';
import { UserSessionContext } from '../contexts/UserSessionContext';
import { sessionStorageService } from '../utils/helpers/sessionStorageService';

interface UserSessionProviderProps {
  children: React.ReactNode;
}

type ProviderReducer = Pick<UserSessionContextState, 'userSession' | 'isReady'>;

const reducerInitialValue: ProviderReducer = {
  isReady: false,
  userSession: null,
};

export const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
  const { accounts, inProgress, instance } = useMsal();
  const [providerReducer, setProviderReducer] = useReducer(
    (prev: ProviderReducer, current: Partial<ProviderReducer>) => ({
      ...prev,
      ...current,
    }),
    reducerInitialValue
  );

  const login = useCallback(() => {
    instance.loginRedirect({ scopes: ['User.Read'] });
  }, [instance]);

  const logout = useCallback(
    () => instance.logout({ account: accounts[0] }),
    [accounts, instance]
  );

  useEffect(() => {
    if (inProgress === 'none') {
      const account = accounts[0]?.idToken ? accounts[0] : null;

      sessionStorageService.set('msal.active.token', account?.idToken ?? '');

      setProviderReducer({
        userSession: account
          ? {
              mail: account.username,
              name: account.name ?? '',
              token: account.idToken ?? '',
              roles: [],
            }
          : null,
        isReady: true,
      });
    }
  }, [accounts, inProgress]);

  const contextValue: UserSessionContextState = useMemo(
    () => ({
      isReady: providerReducer.isReady,
      userSession: providerReducer.userSession,
      login,
      logout,
    }),
    [providerReducer.isReady, providerReducer.userSession, login, logout]
  );

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  );
};
